import { Link } from 'react-router-dom';
import {
  BarChart3,
  BookOpenCheck,
  CircleHelp,
  FileQuestion,
  History,
  Mail,
  ShieldCheck,
  Sparkles,
} from 'lucide-react';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { useAuth } from '../lib/auth';

const HelpPage = () => {
  const { user } = useAuth();

  return (
    <Layout>
      <div className="max-w-4xl mx-auto animate-fadeIn">
        <div className="flex items-start gap-3 mb-8">
          <div className="w-12 h-12 bg-zinc-900 rounded-lg flex items-center justify-center flex-shrink-0">
            <CircleHelp className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-zinc-900">
              Ajuda
            </h1>
            <p className="text-zinc-500">
              Guia rápido para utilizar o Simulados V.O.A.R.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-8">
          <Button asChild variant="outline" className="h-auto justify-start p-4">
            <Link to="/dashboard">
              <FileQuestion className="w-5 h-5 mr-3" />
              <span className="text-left">
                <span className="block font-semibold">Iniciar simulado</span>
                <span className="block text-xs text-zinc-500 font-normal">Escolher uma matéria</span>
              </span>
            </Link>
          </Button>
          <Button asChild variant="outline" className="h-auto justify-start p-4">
            <Link to="/history">
              <History className="w-5 h-5 mr-3" />
              <span className="text-left">
                <span className="block font-semibold">Ver histórico</span>
                <span className="block text-xs text-zinc-500 font-normal">Revisar simulados anteriores</span>
              </span>
            </Link>
          </Button>
          <Button asChild variant="outline" className="h-auto justify-start p-4">
            <Link to="/stats">
              <BarChart3 className="w-5 h-5 mr-3" />
              <span className="text-left">
                <span className="block font-semibold">Estatísticas</span>
                <span className="block text-xs text-zinc-500 font-normal">Acompanhar sua evolução</span>
              </span>
            </Link>
          </Button>
        </div>

        <div className="bg-white border border-zinc-200 rounded-lg p-5 sm:p-6 mb-6">
          <div className="flex items-center gap-3 mb-5">
            <BookOpenCheck className="w-5 h-5 text-zinc-700" />
            <h2 className="text-lg font-bold text-zinc-900">Como utilizar</h2>
          </div>

          <div className="space-y-5">
            {[
              ['1', 'Escolha uma matéria', 'Abra Matérias e selecione o conteúdo que deseja praticar. Cada simulado contém até 20 questões.'],
              ['2', 'Responda às questões', 'Marque uma alternativa por questão. Você pode navegar entre elas antes de finalizar.'],
              ['3', 'Finalize o simulado', 'Confira se respondeu tudo e envie suas respostas para ver a pontuação e o gabarito.'],
              ['4', 'Revise seu resultado', 'Veja quais questões acertou ou errou e consulte a resposta correta de cada uma.'],
              ['5', 'Acompanhe sua evolução', 'Use Histórico e Estatísticas para observar resultados, desempenho por matéria e sugestões de estudo.'],
            ].map(([number, title, description]) => (
              <div key={number} className="flex gap-4">
                <div className="w-8 h-8 rounded-full bg-zinc-900 text-white flex items-center justify-center text-sm font-bold flex-shrink-0">
                  {number}
                </div>
                <div>
                  <h3 className="font-semibold text-zinc-900">{title}</h3>
                  <p className="text-sm text-zinc-600 mt-1">{description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white border border-zinc-200 rounded-lg p-5 sm:p-6 mb-6">
          <div className="flex items-center gap-3 mb-3">
            <Sparkles className="w-5 h-5 text-zinc-700" />
            <h2 className="text-lg font-bold text-zinc-900">Agente V.O.A.R.</h2>
          </div>
          <p className="text-sm text-zinc-600 mb-4">
            O Agente V.O.A.R. ajuda a compreender respostas e identificar assuntos que merecem mais estudo.
          </p>
          <ul className="space-y-2 text-sm text-zinc-700">
            <li>• No gabarito, use <strong>Pedir explicação ao Agente V.O.A.R.</strong> para entender uma questão.</li>
            <li>• Em Estatísticas, use <strong>Analisar meus erros</strong> para receber um plano de estudo sugerido.</li>
            <li>• As recomendações servem como apoio ao estudo e não substituem os materiais e orientações do curso.</li>
          </ul>
        </div>

        <div className="bg-white border border-zinc-200 rounded-lg p-5 sm:p-6 mb-6">
          <h2 className="text-lg font-bold text-zinc-900 mb-2">Perguntas frequentes</h2>
          <Accordion type="single" collapsible>
            <AccordionItem value="quiz">
              <AccordionTrigger>Posso sair de um simulado antes de terminar?</AccordionTrigger>
              <AccordionContent className="text-zinc-600">
                O resultado só é salvo depois que você finaliza e envia o simulado. Se sair antes, as respostas atuais podem ser perdidas.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="history">
              <AccordionTrigger>Onde encontro meus simulados anteriores?</AccordionTrigger>
              <AccordionContent className="text-zinc-600">
                Abra a opção Histórico no menu lateral. Clique em qualquer resultado para rever as questões e o gabarito.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="score">
              <AccordionTrigger>Qual é a pontuação mínima para aprovação?</AccordionTrigger>
              <AccordionContent className="text-zinc-600">
                O aplicativo considera aprovado o resultado com aproveitamento igual ou superior a 70%.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="password">
              <AccordionTrigger>Como altero minha senha?</AccordionTrigger>
              <AccordionContent className="text-zinc-600">
                Clique no seu e-mail no menu lateral e escolha Alterar Senha. Na tela de login, também existe a opção Esqueci minha senha.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="access">
              <AccordionTrigger>Como vejo meu período de acesso?</AccordionTrigger>
              <AccordionContent className="text-zinc-600">
                Os dias restantes aparecem abaixo do seu e-mail no menu lateral.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>

        {user?.is_admin && (
          <div className="bg-white border border-zinc-200 rounded-lg p-5 sm:p-6 mb-6">
            <div className="flex items-center gap-3 mb-3">
              <ShieldCheck className="w-5 h-5 text-zinc-700" />
              <h2 className="text-lg font-bold text-zinc-900">Área administrativa</h2>
            </div>
            <p className="text-sm text-zinc-600">
              Use Questões para cadastrar, editar, excluir, importar ou exportar perguntas; Revisões para analisar sinalizações do Agente V.O.A.R.; e Usuários para gerenciar acessos e consultar históricos.
            </p>
          </div>
        )}

        <div className="rounded-lg border border-zinc-200 bg-zinc-50 p-5 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h2 className="font-bold text-zinc-900">Ainda precisa de ajuda?</h2>
              <p className="text-sm text-zinc-600 mt-1">
                Entre em contato com o suporte da Academia de Aviação.
              </p>
            </div>
            <Button asChild variant="outline">
              <a href="mailto:suporte@academiadeaviacao.com">
                <Mail className="w-4 h-4 mr-2" />
                Falar com o suporte
              </a>
            </Button>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default HelpPage;
